import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Target,
  TrendingUp,
  Eye,
  Edit3,
  Trash2,
  Copy,
  Mail,
  Phone,
  MapPin,
  Star,
  Sparkles,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Settings,
  Download,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
  CheckSquare,
  Square,
  List,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import "../../../styles/trip-cards.css";

const CRMPipelineManagement = () => {
  // State Management
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTrips, setSelectedTrips] = useState([]);
  const [viewMode, setViewMode] = useState("pipeline"); // pipeline, list, analytics
  const [filters, setFilters] = useState({
    stage: "all",
    priority: "all",
    destination: "all",
    dateRange: "all",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draggedTrip, setDraggedTrip] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3); // Show 3 trips per page
  
  const [analytics, setAnalytics] = useState({
    totalValue: 0,
    conversionRate: 0,
    avgDealSize: 0,
    stageDistribution: {},
  });

  // Pipeline stages configuration
  const pipelineStages = [
    {
      id: "lead",
      name: "Lead",
      description: "Initial inquiry received",
      color: "blue",
      icon: Target,
      order: 1,
    },
    {
      id: "qualified",
      name: "Qualified",
      description: "Needs identified, budget confirmed",
      color: "purple",
      icon: CheckCircle,
      order: 2,
    },
    {
      id: "proposal",
      name: "Proposal",
      description: "Itinerary created, awaiting approval",
      color: "orange",
      icon: FileText,
      order: 3,
    },
    {
      id: "negotiation",
      name: "Negotiation",
      description: "Discussing terms and modifications",
      color: "yellow",
      icon: TrendingUp,
      order: 4,
    },
    {
      id: "closed_won",
      name: "Closed Won",
      description: "Trip confirmed and booked",
      color: "green",
      icon: CheckCircle,
      order: 5,
    },
    {
      id: "closed_lost",
      name: "Closed Lost",
      description: "Deal lost or cancelled",
      color: "red",
      icon: X,
      order: 6,
    },
  ];

  // Simulate data loading
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock data - replace with actual API calls
      const mockTrips = [
        {
          id: 1,
          tripName: "Johnson Family Disney World",
          clientName: "Sarah Johnson",
          clientEmail: "sarah.johnson@email.com",
          clientPhone: "(555) 123-4567",
          destination: "Disney World",
          stage: "proposal",
          priority: "high",
          estimatedValue: 8500,
          probability: 75,
          createdDate: "2024-01-10",
          lastActivity: "2024-01-15",
          nextFollowUp: "2024-01-20",
          tags: ["VIP", "Family", "Luxury"],
          notes: "VIP client, prefers luxury accommodations",
          questionnaireLink: "https://parkpro.com/questionnaire/abc123",
          avatar: "SJ",
          location: "New York, NY",
        },
        {
          id: 2,
          tripName: "Chen Universal Adventure",
          clientName: "Mike Chen",
          clientEmail: "mike.chen@email.com",
          clientPhone: "(555) 234-5678",
          destination: "Universal Studios",
          stage: "qualified",
          priority: "medium",
          estimatedValue: 3200,
          probability: 60,
          createdDate: "2024-01-12",
          lastActivity: "2024-01-14",
          nextFollowUp: "2024-01-18",
          tags: ["Family", "New Lead"],
          notes: "Interested in family packages",
          questionnaireLink: "https://parkpro.com/questionnaire/def456",
          avatar: "MC",
          location: "Los Angeles, CA",
        },
        {
          id: 3,
          tripName: "Thompson Family Reunion",
          clientName: "Thompson Family",
          clientEmail: "thompson.family@email.com",
          clientPhone: "(555) 345-6789",
          destination: "Disney World",
          stage: "closed_won",
          priority: "high",
          estimatedValue: 12000,
          probability: 100,
          createdDate: "2024-01-08",
          lastActivity: "2024-01-16",
          nextFollowUp: "2024-02-01",
          tags: ["Family", "Group", "Repeat Customer"],
          notes: "Large family, group bookings",
          questionnaireLink: "https://parkpro.com/questionnaire/ghi789",
          avatar: "TF",
          location: "Chicago, IL",
        },
        {
          id: 4,
          tripName: "Williams Corporate Retreat",
          clientName: "Williams Group",
          clientEmail: "williams@email.com",
          clientPhone: "(555) 456-7890",
          destination: "Disneyland",
          stage: "lead",
          priority: "low",
          estimatedValue: 5000,
          probability: 25,
          createdDate: "2024-01-14",
          lastActivity: "2024-01-14",
          nextFollowUp: "2024-01-21",
          tags: ["Corporate", "Group", "Planning"],
          notes: "Corporate group, planning Q2 trip",
          questionnaireLink: "https://parkpro.com/questionnaire/jkl012",
          avatar: "WG",
          location: "Miami, FL",
        },
        {
          id: 5,
          tripName: "Davis Anniversary Trip",
          clientName: "Robert Davis",
          clientEmail: "robert.davis@email.com",
          clientPhone: "(555) 567-8901",
          destination: "Disney World",
          stage: "negotiation",
          priority: "medium",
          estimatedValue: 4200,
          probability: 85,
          createdDate: "2024-01-11",
          lastActivity: "2024-01-16",
          nextFollowUp: "2024-01-19",
          tags: ["Anniversary", "Romance", "Luxury"],
          notes: "Anniversary celebration, wants romantic dinner",
          questionnaireLink: "https://parkpro.com/questionnaire/mno345",
          avatar: "RD",
          location: "Austin, TX",
        },
        {
          id: 6,
          tripName: "Miller Kids Adventure",
          clientName: "Jennifer Miller",
          clientEmail: "jennifer.miller@email.com",
          clientPhone: "(555) 678-9012",
          destination: "Universal Studios",
          stage: "closed_lost",
          priority: "low",
          estimatedValue: 2800,
          probability: 0,
          createdDate: "2024-01-09",
          lastActivity: "2024-01-13",
          nextFollowUp: null,
          tags: ["Family", "Kids", "Budget"],
          notes: "Decided to book directly with Universal",
          questionnaireLink: "https://parkpro.com/questionnaire/pqr678",
          avatar: "JM",
          location: "Denver, CO",
        },
      ];

      setTrips(mockTrips);
      
      // Calculate analytics
      const totalValue = mockTrips.reduce((sum, trip) => sum + trip.estimatedValue, 0);
      const wonTrips = mockTrips.filter(trip => trip.stage === "closed_won").length;
      const conversionRate = (wonTrips / mockTrips.length) * 100;
      const avgDealSize = totalValue / mockTrips.length;
      
      const stageDistribution = {};
      pipelineStages.forEach(stage => {
        stageDistribution[stage.id] = mockTrips.filter(trip => trip.stage === stage.id).length;
      });

      setAnalytics({
        totalValue,
        conversionRate,
        avgDealSize,
        stageDistribution,
      });
      
      setLoading(false);
    };

    loadData();
  }, []);

  // Filter and group trips by stage
  const { filteredTripsByStage, paginatedTripsByStage, totalPages } = useMemo(() => {
    const filtered = trips.filter(trip => {
      const matchesSearch = searchQuery === "" || 
        trip.tripName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.clientEmail.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStage = filters.stage === "all" || trip.stage === filters.stage;
      const matchesPriority = filters.priority === "all" || trip.priority === filters.priority;
      const matchesDestination = filters.destination === "all" || trip.destination === filters.destination;

      return matchesSearch && matchesStage && matchesPriority && matchesDestination;
    });

    // Group by stage
    const grouped = {};
    const paginated = {};
    let maxTripsInStage = 0;
    
    pipelineStages.forEach(stage => {
      grouped[stage.id] = filtered.filter(trip => trip.stage === stage.id);
      maxTripsInStage = Math.max(maxTripsInStage, grouped[stage.id].length);
    });

    // Calculate pagination
    const totalPages = Math.ceil(maxTripsInStage / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Apply pagination to each stage
    pipelineStages.forEach(stage => {
      paginated[stage.id] = grouped[stage.id].slice(startIndex, endIndex);
    });

    return { 
      filteredTripsByStage: grouped, 
      paginatedTripsByStage: paginated, 
      totalPages 
    };
  }, [trips, searchQuery, filters, currentPage, itemsPerPage]);

  // Handle drag and drop
  const handleDragStart = (e, tripId) => {
    setDraggedTrip(tripId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, newStage) => {
    e.preventDefault();
    if (draggedTrip && newStage) {
      // Update trip stage
      setTrips(prevTrips => 
        prevTrips.map(trip => 
          trip.id === draggedTrip 
            ? { ...trip, stage: newStage }
            : trip
        )
      );
      setDraggedTrip(null);
    }
  };

  // Handle bulk operations
  const handleSelectAll = () => {
    const allTripIds = Object.values(filteredTripsByStage).flat().map(trip => trip.id);
    if (selectedTrips.length === allTripIds.length) {
      setSelectedTrips([]);
    } else {
      setSelectedTrips(allTripIds);
    }
  };

  const handleSelectTrip = (tripId) => {
    setSelectedTrips(prev => 
      prev.includes(tripId) 
        ? prev.filter(id => id !== tripId)
        : [...prev, tripId]
    );
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on trips:`, selectedTrips);
    // Implement bulk actions here
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  // Get priority color and icon
  const getPriorityInfo = (priority) => {
    switch (priority) {
      case "high":
        return { color: "red", label: "High", icon: ArrowUpRight };
      case "medium":
        return { color: "yellow", label: "Medium", icon: TrendingUp };
      case "low":
        return { color: "green", label: "Low", icon: ArrowDownRight };
      default:
        return { color: "gray", label: "Normal", icon: TrendingUp };
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-pipeline-loading">
        <div className="crm-loading-spinner">
          <RefreshCw className="animate-spin" size={48} />
          <p className="crm-loading-text">Loading pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >

      {/* Analytics Cards */}
      <motion.div variants={itemVariants} className="crm-analytics-grid">
        <div className="crm-analytics-card crm-card-blue">
          <div className="crm-analytics-icon">
            <DollarSign size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">${analytics.totalValue.toLocaleString()}</div>
            <div className="crm-analytics-label">Pipeline Value</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-green">
          <div className="crm-analytics-icon">
            <TrendingUp size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{analytics.conversionRate.toFixed(1)}%</div>
            <div className="crm-analytics-label">Conversion Rate</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-purple">
          <div className="crm-analytics-icon">
            <BarChart3 size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">${analytics.avgDealSize.toLocaleString()}</div>
            <div className="crm-analytics-label">Avg Deal Size</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-orange">
          <div className="crm-analytics-icon">
            <Target size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{trips.length}</div>
            <div className="crm-analytics-label">Active Deals</div>
          </div>
        </div>
      </motion.div>

      {/* Controls */}
      <motion.div variants={itemVariants} className="crm-pipeline-controls">
        <div className="crm-controls-left">
          <div className="crm-search-container">
            <Search className="crm-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search trips..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="crm-search-input"
            />
          </div>
          
          <button
            className={`crm-filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            <span>Filters</span>
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          <div className="crm-view-toggle">
            <button
              className={`crm-view-btn ${viewMode === 'pipeline' ? 'active' : ''}`}
              onClick={() => setViewMode('pipeline')}
            >
              <BarChart3 size={18} />
            </button>
            <button
              className={`crm-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
            </button>
            <button
              className={`crm-view-btn ${viewMode === 'analytics' ? 'active' : ''}`}
              onClick={() => setViewMode('analytics')}
            >
              <TrendingUp size={18} />
            </button>
          </div>
        </div>

        <div className="crm-controls-right">
          {selectedTrips.length > 0 && (
            <div className="crm-bulk-actions">
              <span className="crm-bulk-count">
                {selectedTrips.length} selected
              </span>
              <button
                className="crm-bulk-btn"
                onClick={() => handleBulkAction('email')}
              >
                <Mail size={16} />
                Email
              </button>
              <button
                className="crm-bulk-btn"
                onClick={() => handleBulkAction('export')}
              >
                <Download size={16} />
                Export
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Advanced Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="crm-filters-panel"
          >
            <div className="crm-filters-grid">
              <div className="crm-filter-group">
                <label className="crm-filter-label">Stage</label>
                <select
                  value={filters.stage}
                  onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}
                  className="crm-filter-select"
                >
                  <option value="all">All Stages</option>
                  {pipelineStages.map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </div>

              <div className="crm-filter-group">
                <label className="crm-filter-label">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                  className="crm-filter-select"
                >
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div className="crm-filter-group">
                <label className="crm-filter-label">Destination</label>
                <select
                  value={filters.destination}
                  onChange={(e) => setFilters(prev => ({ ...prev, destination: e.target.value }))}
                  className="crm-filter-select"
                >
                  <option value="all">All Destinations</option>
                  <option value="Disney World">Disney World</option>
                  <option value="Disneyland">Disneyland</option>
                  <option value="Universal Studios">Universal Studios</option>
                </select>
              </div>

              <div className="crm-filter-group">
                <label className="crm-filter-label">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="crm-filter-select"
                >
                  <option value="all">All Time</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Count */}
      <motion.div variants={itemVariants} className="crm-results-info">
        <span className="crm-results-count">
          {Object.values(filteredTripsByStage).flat().length} of {trips.length} trips
        </span>
        {selectedTrips.length > 0 && (
          <span className="crm-selected-count">
            {selectedTrips.length} selected
          </span>
        )}
      </motion.div>

      {/* Pipeline View */}
      {viewMode === "pipeline" && (
        <motion.div variants={itemVariants} className="crm-pipeline-container">
          {/* Select All Header */}
          <div className="crm-select-all-header">
            <button
              className="crm-select-all-btn"
              onClick={handleSelectAll}
            >
              {selectedTrips.length === Object.values(filteredTripsByStage).flat().length ? (
                <CheckSquare size={18} />
              ) : (
                <Square size={18} />
              )}
              <span>Select All</span>
            </button>
          </div>

          {/* Pipeline Stages */}
          <div className="crm-pipeline-stages">
            {pipelineStages.map((stage) => {
              const stageTrips = paginatedTripsByStage[stage.id] || [];
              const totalStageTrips = filteredTripsByStage[stage.id]?.length || 0;
              const StageIcon = stage.icon;
              const totalValue = stageTrips.reduce((sum, trip) => sum + trip.estimatedValue, 0);

              return (
                <motion.div
                  key={stage.id}
                  variants={itemVariants}
                  className={`crm-pipeline-stage crm-stage-${stage.color}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Stage Header */}
                  <div className="crm-stage-header">
                    <div className="crm-stage-info">
                      <div className="crm-stage-icon">
                        <StageIcon size={20} />
                      </div>
                    </div>
                    <div className="crm-stage-stats">
                      <div className="crm-stage-count">{stageTrips.length}</div>
                      <div className="crm-stage-value">${totalValue.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Add Trip Button */}
                  <button className="crm-add-trip-btn">
                    <Plus size={16} />
                    <span>Add Trip</span>
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="crm-pagination">
              <button
                className="crm-pagination-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              
              <div className="crm-pagination-info">
                <span className="crm-pagination-text">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="crm-pagination-dots">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`crm-pagination-dot ${currentPage === i + 1 ? 'active' : ''}`}
                      onClick={() => setCurrentPage(i + 1)}
                    />
                  ))}
                </div>
              </div>
              
              <button
                className="crm-pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Empty State */}
      {Object.values(filteredTripsByStage).flat().length === 0 && (
        <motion.div variants={itemVariants} className="crm-empty-state">
          <div className="crm-empty-icon">
            <Target size={64} />
          </div>
          <h3 className="crm-empty-title">No Trips Found</h3>
          <p className="crm-empty-description">
            {searchQuery || Object.values(filters).some(f => f !== "all")
              ? "Try adjusting your search criteria or filters."
              : "Get started by creating your first trip."
            }
          </p>
          <button className="crm-action-btn crm-action-primary">
            <Plus size={18} />
            <span>Create First Trip</span>
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CRMPipelineManagement;
